/*
 * Piezas pequeñas que aparecen en todas las pantallas. Cada una es la única fuente
 * de verdad de su trato visual: si el diseño de una etapa o de una fase cambia,
 * cambia aquí y en ningún otro lado.
 */

import type { CSSProperties, ReactNode } from "react";

import { ETAPAS, FASES, type Etapa, type EstadoItem } from "../api/tipos";

export function Icono({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

/** Etapa de una cuenta: color + etiqueta, siempre juntos. */
export function PastillaEtapa({ etapa }: { etapa: Etapa }) {
  const e = ETAPAS[etapa];
  return <span className="pastilla" style={{ background: e.bg, color: e.color }}>{e.label}</span>;
}

/** Cualquier otro estado con color: se pasa el color de estado y el texto. */
export function Pastilla({ estado, children }: { estado: EstadoItem | "muted"; children: ReactNode }) {
  const map: Record<string, [string, string]> = {
    proposed: ["var(--proposed)", "var(--proposed-bg)"],
    verified: ["var(--verified)", "var(--verified-bg)"],
    risk: ["var(--risk)", "var(--risk-bg)"],
    conflict: ["var(--conflict)", "var(--conflict-bg)"],
    discarded: ["var(--discarded)", "var(--sunken)"],
    sky: ["var(--sky)", "#E8F0FE"],
    muted: ["var(--muted)", "var(--sunken)"],
  };
  const [color, bg] = map[estado];
  return <span className="pastilla" style={{ color, background: bg }}>{children}</span>;
}

export function Punto({ estado }: { estado: EstadoItem }) {
  const color = estado === "sky" ? "var(--sky)" : `var(--${estado})`;
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 6 }} />;
}

/** Barra de 7 segmentos D0–D6. La fase actual en sky, las pasadas en ice. */
export function BarraFase({ fase, ancho = 112, mostrarId = true }: { fase: number; ancho?: number | "100%"; mostrarId?: boolean }) {
  const f = FASES[fase] ?? FASES[0];
  return (
    <span title={`${f.id} · ${f.nombre}`} style={{ display: ancho === "100%" ? "flex" : "inline-flex", width: ancho === "100%" ? "100%" : "auto", alignItems: "center", gap: 8 }}>
      <span style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, width: ancho, flex: ancho === "100%" ? 1 : "none" }}>
        {FASES.map((p, i) => (
          <span key={p.id} style={{ height: 6, borderRadius: 2, background: i === fase ? "var(--sky)" : i < fase ? "var(--ice)" : "var(--border)" }} />
        ))}
      </span>
      {mostrarId && <span className="mono" style={{ color: "var(--text)", fontWeight: 500 }}>{f.id}</span>}
    </span>
  );
}

/** Marcador discreto de origen relacional: es cuenta real, pero no cuenta en conversión. */
export function MarcaRelacional({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span title="Origen relacional — no se promedia en métricas de conversión"
      style={{ display: "inline-block", width: 14, height: 14, borderRadius: 3, border: "1px solid var(--border-strong)", color: "var(--faint)", fontSize: 9, lineHeight: "12px", textAlign: "center", fontFamily: "var(--font-mono)", marginLeft: 6, verticalAlign: "middle" }}>r</span>
  );
}

export function Fit({ v }: { v: number }) {
  const c = v >= 75 ? "var(--verified)" : v >= 50 ? "var(--text)" : "var(--faint)";
  return <span className="mono" style={{ color: c, fontWeight: v >= 75 ? 500 : 400 }}>{v}</span>;
}

/** El numerado en cuadro del canvas: 17px, radio 5, sky. Solo para secuencias reales. */
export function Num({ n, hecho, activo }: { n: number | string; hecho?: boolean; activo?: boolean }) {
  return (
    <span className="mono" style={{ width: 17, height: 17, borderRadius: 5, flexShrink: 0, display: "grid", placeItems: "center", fontSize: 10.5, lineHeight: 1, color: "#fff",
      background: hecho ? "var(--verified)" : activo ? "var(--sky)" : "var(--border-strong)" }}>{hecho ? "✓" : n}</span>
  );
}

/** Trato "abierto": sin resolver, esperando a una persona. */
export function Abierto({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div className="abierto" style={{ padding: "12px 14px", fontSize: 13.5, lineHeight: 1.5, ...style }}>{children}</div>;
}

export function Clave({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span className="tag11" style={{ color: "var(--faint)" }}>{k}</span>
      <span style={{ fontSize: 14 }}>{children}</span>
    </div>
  );
}

export function CabeceraSeccion({ titulo, n, derecha }: { titulo: string; n?: number; derecha?: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <h2 className="seccion-h" style={{ whiteSpace: "nowrap" }}>
        {titulo}{n !== undefined && <span className="mono" style={{ color: "var(--faint)", marginLeft: 6 }}>{n}</span>}
      </h2>
      {derecha}
    </div>
  );
}

export function Esqueleto({ lineas = 3, alto = 14 }: { lineas?: number; alto?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {Array.from({ length: lineas }, (_, i) => <div key={i} className="esqueleto" style={{ height: alto, width: `${100 - (i % 3) * 15}%` }} />)}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <span className="kbd">{children}</span>;
}

// Fechas: el modelo guarda ISO; la gente lee "12 sep".
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
export function fmtFecha(iso: string | null | undefined, conDia = false): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T12:00:00" : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const base = `${d.getDate()} ${MESES[d.getMonth()]}`;
  return conDia ? `${DIAS[d.getDay()]} ${base}` : base;
}
export function fmtFechaLarga(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  const dias = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
  const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`;
}
// Fecha local, no UTC: a las 7 de la tarde en Guadalajara todavía no es mañana.
export function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function fmtMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
export function fmtMXN(n: number): string {
  return `MXN ${n.toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}
